import { useState, useEffect, useCallback } from 'react';
import { 
  ReaderVersion, 
  ReaderVersionSearchParams, 
  ReaderVersionFiltersState,
  PlatformStats 
} from '../types';
import readerVersionService from '../services/readerVersionService';

export const useReaderVersion = () => {
  const [versions, setVersions] = useState<ReaderVersion[]>([]);
  const [filteredVersions, setFilteredVersions] = useState<ReaderVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<ReaderVersion | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Filters state
  const [filters, setFilters] = useState<ReaderVersionFiltersState>({
    platform: '',
    channel: '',
    mandatory: '',
    active: '',
    version: '',
    from_date: '',
    to_date: ''
  });

  // Load versions with pagination
  const loadVersions = useCallback(async (page = 1, searchParams?: ReaderVersionSearchParams) => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        page,
        per_page: pagination.per_page,
        ...searchParams
      };

      const response = await readerVersionService.getVersions(params);
      
      if (response.success) {
        setVersions(response.data);
        setFilteredVersions(response.data);
        setPagination(response.pagination);
      } else {
        throw new Error('Failed to load versions');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load versions';
      setError(errorMessage);
      console.error('Error loading versions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.per_page]);

  // Load platform statistics
  const loadPlatformStats = useCallback(async () => {
    try {
      const response = await readerVersionService.getPlatformStats();
      
      if (response.success) {
        setPlatformStats(response.data);
      }
    } catch (err) {
      console.error('Error loading platform stats:', err);
    }
  }, []);

  // Search versions
  const searchVersions = useCallback(async (searchParams: ReaderVersionSearchParams) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await readerVersionService.searchVersions(searchParams);
      
      if (response.success) {
        setVersions(response.data);
        setFilteredVersions(response.data);
      } else {
        throw new Error('Failed to search versions');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search versions';
      setError(errorMessage);
      console.error('Error searching versions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create version
  const createVersion = useCallback(async (data: Partial<ReaderVersion>) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await readerVersionService.createVersion(data);
      
      if (response.success) {
        // Reload versions to get updated list
        await Promise.all([loadVersions(pagination.current_page), loadPlatformStats()]);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create version');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create version';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadVersions, loadPlatformStats, pagination.current_page]);

  // Update version
  const updateVersion = useCallback(async (id: string, data: Partial<ReaderVersion>) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await readerVersionService.updateVersion(id, data);
      
      if (response.success) {
        // Nạp lại từ API, KHÔNG thay dòng bằng response.data: PUT trả
        // `data: true` (BaseRepository::update trả kết quả save()), thay vào là
        // dòng đó vỡ tới khi F5.
        await Promise.all([loadVersions(pagination.current_page), loadPlatformStats()]);
        if (selectedVersion?.id === id) setSelectedVersion(null);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update version');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update version';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedVersion, loadVersions, loadPlatformStats, pagination.current_page]);

  // Delete version
  const deleteVersion = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await readerVersionService.deleteVersion(id);
      
      if (response.success) {
        // Remove from local state
        setVersions(prev => prev.filter(v => v.id !== id));
        setFilteredVersions(prev => prev.filter(v => v.id !== id));
        
        if (selectedVersion?.id === id) {
          setSelectedVersion(null);
        }
        void loadPlatformStats();
      } else {
        throw new Error(response.message || 'Failed to delete version');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete version';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedVersion, loadPlatformStats]);

  // Get version by ID
  const getVersion = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await readerVersionService.getVersion(id);
      
      if (response.success) {
        setSelectedVersion(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to get version');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get version';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Apply filters
  const applyFilters = useCallback((newFilters: Partial<ReaderVersionFiltersState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    
    // Convert filters to search params
    const searchParams: ReaderVersionSearchParams = {};
    
    if (newFilters.platform && newFilters.platform !== '') {
      searchParams.platform = newFilters.platform;
    }

    // API chỉ lọc được kênh khi đã biết nền tảng: cùng tên kênh ở hai nền tảng
    // khác nhau là hai thứ khác nhau
    if (newFilters.channel && newFilters.channel !== '' && searchParams.platform) {
      searchParams.channel = newFilters.channel;
    }
    
    if (newFilters.mandatory && newFilters.mandatory !== '') {
      searchParams.mandatory = newFilters.mandatory === 'true';
    }
    
    if (newFilters.active && newFilters.active !== '') {
      searchParams.active = newFilters.active === 'true';
    }
    
    if (newFilters.version && newFilters.version !== '') {
      searchParams.version = newFilters.version;
    }
    
    if (newFilters.from_date && newFilters.from_date !== '') {
      searchParams.from_date = newFilters.from_date;
    }
    
    if (newFilters.to_date && newFilters.to_date !== '') {
      searchParams.to_date = newFilters.to_date;
    }

    // Load versions with filters
    loadVersions(1, searchParams);
  }, [loadVersions]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({
      platform: '',
      channel: '',
      mandatory: '',
      active: '',
      version: '',
      from_date: '',
      to_date: ''
    });
    loadVersions(1);
  }, [loadVersions]);

  // Initial load
  useEffect(() => {
    loadVersions();
    loadPlatformStats();
  }, [loadVersions, loadPlatformStats]);

  return {
    // Data
    versions,
    filteredVersions,
    selectedVersion,
    platformStats,
    pagination,
    filters,
    
    // State
    isLoading,
    error,
    
    // Actions
    loadVersions,
    searchVersions,
    createVersion,
    updateVersion,
    deleteVersion,
    getVersion,
    loadPlatformStats,
    applyFilters,
    clearFilters,
    setSelectedVersion,
    setError
  };
};
